import { usePeriodName } from '@app/src/hooks/usePeriodName'
import { ChartType } from '@app/src/pages/Assessments/ChartTypeSelector'
import {
  DONT_HAVE_THIS_COLOR,
  notAnsweredStyling,
  REPORTED_COLOR,
} from '@app/src/pages/ResourceCollection/Collections/DataHub/DataInsights/Charts/CategoryChartsConfigs/categoryChartConfigsUtils'
import type { CartesianAxisOption } from '@app/src/types/echarts'
import { getMinMaxOnYAxisCountCharts } from '@app/src/utils/statisticsUtils'
import { darken, useTheme } from '@mui/material'
import type { BarSeriesOption, EChartsOption } from 'echarts'
import type { XAXisOption, YAXisOption } from 'echarts/types/dist/shared'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { StatisticsSummaryPerGroup } from '../DataHubSummaryChart'

interface PeriodSummaryChartConfigsParams {
  resultsForGroup: StatisticsSummaryPerGroup[]
  selectedChartType: ChartType
}

export enum SummaryChartDataTypes {
  CompaniesAnswered = 'companiesAnswered',
  CompaniesAnsweredDontHaveThis = 'companiesAnsweredDontHaveThis',
  CompaniesNotAnswered = 'companiesNotAnswered',
}

const getMinMaxYAxis = (optionItemResult: StatisticsSummaryPerGroup[]) => {
  const summaryValues = optionItemResult.flatMap(
    res => res.companiesAnswered + res.companiesAnsweredDontHaveThis + res.companiesNotAnswered,
  )
  return getMinMaxOnYAxisCountCharts(summaryValues)
}

export const usePeriodSummaryChartConfigs = ({
  resultsForGroup,
  selectedChartType,
}: PeriodSummaryChartConfigsParams) => {
  const { typography, palette } = useTheme()
  const { formatMessage } = useIntl()
  const { formatPeriodName } = usePeriodName()

  const isHorizontalBarChart = selectedChartType === ChartType.Horizontal

  const isSingleGroup = resultsForGroup.length === 1

  if (isHorizontalBarChart) {
    resultsForGroup = resultsForGroup.reverse()
  }

  const companiesAnsweredData = resultsForGroup.map(r => ({
    name: formatPeriodName(r.groupName),
    value: r.companiesAnswered,
    groupName: r.groupName,
    summaryType: SummaryChartDataTypes.CompaniesAnswered,
    emphasis: { itemStyle: { color: darken(REPORTED_COLOR, 0.2) } },
    label: isHorizontalBarChart
      ? {
          show: true,
          position: [0, -14] as [number, number],
          align: 'left' as const,
          formatter: '{b}',
          fontFamily: typography.fontFamily,
          fontSize: typography.body2.fontSize,
        }
      : undefined,
  }))

  const companiesAnsweredDontHaveThis = resultsForGroup.map(r => ({
    name: formatPeriodName(r.groupName),
    value: r.companiesAnsweredDontHaveThis,
    groupName: r.groupName,
    summaryType: SummaryChartDataTypes.CompaniesAnsweredDontHaveThis,

    emphasis: { itemStyle: { color: darken(DONT_HAVE_THIS_COLOR, 0.2) } },
  }))

  const companiesNotAnswered = resultsForGroup.map(r => ({
    name: formatPeriodName(r.groupName),
    value: r.companiesNotAnswered,
    groupName: r.groupName,
    summaryType: SummaryChartDataTypes.CompaniesNotAnswered,
  }))

  const existingOptions = useMemo(() => resultsForGroup.map(f => f.groupName), [resultsForGroup])

  const { min, max } = getMinMaxYAxis(resultsForGroup)

  const valueAxis: CartesianAxisOption = useMemo(
    () => ({
      type: 'value',
      name: formatMessage({ id: 'statistics.dataInsights.count' }),
      nameLocation: 'middle',
      nameGap: 35,
      min: min,
      max: max,
      minInterval: 1,
      nameTextStyle: {
        fontFamily: typography.fontFamily,
        fontSize: typography.body2.fontSize,
      },
    }),
    [formatMessage, typography.body2.fontSize, typography.fontFamily, min, max],
  )

  const categoryAxis: CartesianAxisOption = useMemo(
    () => ({
      type: 'category',
      data: existingOptions,
      axisLabel: {
        interval: 0,
        fontFamily: typography.fontFamily,
        fontSize: typography.body2.fontSize,
        formatter: (value: string | number) => {
          return formatPeriodName(String(value))
        },
      },
    }),
    [existingOptions, typography.body2.fontSize, typography.fontFamily],
  )

  const reportedSeries: BarSeriesOption = useMemo(
    () => ({
      name: formatMessage(
        { id: 'statistics.dataInsights.reportedCount' },
        { count: String(companiesAnsweredData[0]?.value ?? 0), isSinglePeriod: isSingleGroup },
      ),
      type: 'bar',
      data: companiesAnsweredData,
      color: REPORTED_COLOR,
      ...(isHorizontalBarChart ? { barCategoryGap: '50%' } : {}),
    }),
    [formatMessage, companiesAnsweredData, isSingleGroup, isHorizontalBarChart],
  )

  const dontHaveThisSeries: BarSeriesOption = useMemo(
    () => ({
      name: formatMessage(
        { id: 'statistics.dataInsights.dontHaveThisCount' },
        { count: String(companiesAnsweredDontHaveThis[0]?.value ?? 0), isSinglePeriod: isSingleGroup },
      ),
      type: 'bar',
      data: companiesAnsweredDontHaveThis,
      color: DONT_HAVE_THIS_COLOR,
    }),
    [formatMessage, companiesAnsweredDontHaveThis, isSingleGroup],
  )

  const noResponseSeries: BarSeriesOption = useMemo(
    () => ({
      name: formatMessage(
        { id: 'statistics.dataInsights.noResponseCount' },
        { count: String(companiesNotAnswered[0]?.value ?? 0), isSinglePeriod: isSingleGroup },
      ),
      type: 'bar',
      data: companiesNotAnswered,
      ...notAnsweredStyling(palette),
    }),
    [formatMessage, companiesNotAnswered, isSingleGroup, isHorizontalBarChart, palette],
  )

  const chartOptions: EChartsOption = useMemo(() => {
    const xAxis = isHorizontalBarChart ? (valueAxis as XAXisOption) : (categoryAxis as XAXisOption)
    const yAxis = isHorizontalBarChart ? ({ ...categoryAxis, show: false } as YAXisOption) : (valueAxis as YAXisOption)

    // Only include series that have data
    const hasSkippedData = resultsForGroup.some(res => res.companiesAnsweredDontHaveThis > 0)
    const hasNoResponseData = resultsForGroup.some(res => res.companiesNotAnswered > 0)

    const series = [
      reportedSeries,
      ...(hasSkippedData ? [dontHaveThisSeries] : []),
      ...(hasNoResponseData ? [noResponseSeries] : []),
    ]

    return {
      tooltip: {
        axisPointer: {
          type: 'shadow',
        },
      },
      grid: {
        containLabel: !isHorizontalBarChart,
        left: isHorizontalBarChart ? 20 : 40,
        right: 7,
        top: 30,
        bottom: isHorizontalBarChart ? 90 : 50,
      },
      xAxis,
      yAxis,
      legend: {
        icon: 'circle',
        bottom: 0,
        left: 0,
        textStyle: {
          fontFamily: typography.fontFamily,
          fontSize: typography.body2.fontSize,
        },
      },
      series: series,
    }
  }, [
    isHorizontalBarChart,
    valueAxis,
    categoryAxis,
    typography.fontFamily,
    typography.body2.fontSize,
    reportedSeries,
    dontHaveThisSeries,
    noResponseSeries,
  ])

  return chartOptions
}
